import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Edit, Clock, CalendarDays, RefreshCw, ChefHat, Star, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

const ROLES = ['Mesero', 'Capitán', 'Chef', 'Sous Chef', 'Hostess', 'Barista', 'Bartender', 'Sommelier', 'Gerente', 'Cocina', 'Limpieza'];
const ROLE_COLORS = { Mesero: 'bg-blue-100 text-blue-700', Capitán: 'bg-purple-100 text-purple-700', Chef: 'bg-red-100 text-red-700', 'Sous Chef': 'bg-orange-100 text-orange-700', Hostess: 'bg-pink-100 text-pink-700', Barista: 'bg-amber-100 text-amber-700', Bartender: 'bg-indigo-100 text-indigo-700', Sommelier: 'bg-emerald-100 text-emerald-700', Gerente: 'bg-gray-100 text-gray-700' };
const EMPTY_MEMBER = { name: '', role: 'Mesero', email: '', phone: '', hourly_rate: '', notes: '' };
const EMPTY_SHIFT = { member_id: '', member_name: '', date: new Date().toISOString().slice(0, 10), start_time: '09:00', end_time: '17:00', role: '', notes: '' };
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

export default function StaffScheduling() {
  const [members, setMembers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [shiftForm, setShiftForm] = useState(EMPTY_SHIFT);
  const [saving, setSaving] = useState(false);
  const weekDates = getWeekDates(weekOffset);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/staff/members`, { headers: authHeader() }),
        fetch(`${API_URL}/api/staff/shifts`, { headers: authHeader() }),
      ]);
      if (mRes.ok) setMembers(await mRes.json());
      if (sRes.ok) setShifts(await sRes.json());
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveMember = async () => {
    if (!memberForm.name) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const method = editMember ? 'PUT' : 'POST';
      const url = editMember ? `${API_URL}/api/staff/members/${editMember.id}` : `${API_URL}/api/staff/members`;
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(memberForm) });
      if (res.ok) { toast.success(editMember ? 'Empleado actualizado' : 'Empleado agregado'); setShowMemberModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const saveShift = async () => {
    if (!shiftForm.member_id || !shiftForm.date) { toast.error('Completa los campos requeridos'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/shifts`, { method: 'POST', headers: authHeader(), body: JSON.stringify(shiftForm) });
      if (res.ok) { toast.success('Turno agregado'); setShowShiftModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm('¿Eliminar este empleado y sus turnos?')) return;
    await fetch(`${API_URL}/api/staff/members/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Empleado eliminado'); load();
  };

  const deleteShift = async (id) => {
    await fetch(`${API_URL}/api/staff/shifts/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Turno eliminado'); load();
  };

  const openShiftModal = (date = null, member = null) => {
    const m = member || members[0];
    setShiftForm({
      ...EMPTY_SHIFT,
      date: date || new Date().toISOString().slice(0, 10),
      member_id: m?.id || '',
      member_name: m?.name || '',
      role: m?.role || '',
    });
    setShowShiftModal(true);
  };

  const getShiftsForDay = (date) => shifts.filter(s => s.date === date);
  const totalHoursWeek = shifts.filter(s => weekDates.includes(s.date)).reduce((acc, s) => {
    const [sh, sm] = (s.start_time || '0:00').split(':').map(Number);
    const [eh, em] = (s.end_time || '0:00').split(':').map(Number);
    return acc + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
  }, 0);

  const stats = [
    { label: 'Empleados activos', value: members.filter(m => m.status !== 'inactive').length, icon: Users },
    { label: 'Turnos esta semana', value: shifts.filter(s => weekDates.includes(s.date)).length, icon: CalendarDays },
    { label: 'Horas programadas', value: Math.round(totalHoursWeek), icon: Clock },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Programación de Personal" subtitle="Gestiona empleados y turnos semanales" helpSection="personal" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

        <div className="grid grid-cols-3 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <s.icon className="w-8 h-8 text-orange-500" />
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="schedule">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="schedule">Horario Semanal</TabsTrigger>
              <TabsTrigger value="team">Equipo</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <Button onClick={() => { setEditMember(null); setMemberForm(EMPTY_MEMBER); setShowMemberModal(true); }} variant="outline" className="gap-1"><Plus className="w-4 h-4" /> Empleado</Button>
              <Button onClick={() => openShiftModal()} className="bg-orange-500 hover:bg-orange-600 text-white gap-1"><Plus className="w-4 h-4" /> Turno</Button>
            </div>
          </div>

          <TabsContent value="schedule">
            {/* Week Navigator */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" onClick={() => setWeekOffset(w => w - 1)}>← Semana anterior</Button>
              <span className="text-sm font-medium text-gray-600">{weekDates[0]} — {weekDates[6]}</span>
              <Button variant="outline" onClick={() => setWeekOffset(w => w + 1)}>Semana siguiente →</Button>
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {DAYS.map((day, i) => (
                <div key={i} className="min-h-32">
                  <div className={`text-center text-xs font-bold py-2 px-1 rounded-t-lg mb-1 ${weekDates[i] === new Date().toISOString().slice(0, 10) ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700'}`}>
                    {day}<br /><span className="font-normal opacity-70">{weekDates[i]?.slice(5)}</span>
                  </div>
                  <div className="space-y-1">
                    {getShiftsForDay(weekDates[i]).map(s => (
                      <div key={s.id} className={`text-xs p-1.5 rounded-lg cursor-pointer group relative ${ROLE_COLORS[s.role] || 'bg-gray-100 text-gray-700'}`}>
                        <p className="font-semibold truncate">{s.member_name?.split(' ')[0]}</p>
                        <p className="opacity-70">{s.start_time}–{s.end_time}</p>
                        <button onClick={() => deleteShift(s.id)} className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 text-red-500">×</button>
                      </div>
                    ))}
                    <button onClick={() => openShiftModal(weekDates[i])} className="w-full text-xs text-gray-300 hover:text-orange-500 py-1 border border-dashed border-gray-200 hover:border-orange-300 rounded-lg transition-colors">+ turno</button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="team">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div> :
            members.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay empleados registrados</p>
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setEditMember(null); setMemberForm(EMPTY_MEMBER); setShowMemberModal(true); }}><Plus className="w-4 h-4 mr-1" /> Agregar primer empleado</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {members.map(m => (
                  <Card key={m.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center font-bold text-orange-600 flex-shrink-0">{m.name?.charAt(0)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{m.name}</p>
                        <Badge className={`text-xs mt-0.5 ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-600'}`}>{m.role}</Badge>
                        {m.email && <p className="text-xs text-gray-500 mt-1 truncate">{m.email}</p>}
                        {m.hourly_rate && <p className="text-xs text-gray-500">$ {m.hourly_rate}/hr</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditMember(m); setMemberForm(m); setShowMemberModal(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteMember(m.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Member Modal */}
      <Dialog open={showMemberModal} onOpenChange={setShowMemberModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editMember ? 'Editar Empleado' : 'Nuevo Empleado'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre completo *</Label><Input value={memberForm.name} onChange={e => setMemberForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Rol</Label>
              <Select value={memberForm.role} onValueChange={v => setMemberForm(p => ({ ...p, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div><Label>Teléfono</Label><Input value={memberForm.phone} onChange={e => setMemberForm(p => ({ ...p, phone: e.target.value }))} /></div>
              <div><Label>Tarifa por hora (MXN)</Label><Input type="number" value={memberForm.hourly_rate} onChange={e => setMemberForm(p => ({ ...p, hourly_rate: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMemberModal(false)}>Cancelar</Button>
            <Button onClick={saveMember} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Modal */}
      <Dialog open={showShiftModal} onOpenChange={setShowShiftModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Agregar Turno</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Empleado *</Label>
              <Select value={shiftForm.member_id} onValueChange={v => {
                const m = members.find(x => x.id === v);
                setShiftForm(p => ({ ...p, member_id: v, member_name: m?.name || '', role: m?.role || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empleado..." /></SelectTrigger>
                <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha *</Label><Input type="date" value={shiftForm.date} onChange={e => setShiftForm(p => ({ ...p, date: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Entrada</Label><Input type="time" value={shiftForm.start_time} onChange={e => setShiftForm(p => ({ ...p, start_time: e.target.value }))} /></div>
              <div><Label>Salida</Label><Input type="time" value={shiftForm.end_time} onChange={e => setShiftForm(p => ({ ...p, end_time: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftModal(false)}>Cancelar</Button>
            <Button onClick={saveShift} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Agregar Turno'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
